import type { DirectoryAssigneePreview } from '@/lib/api/directory.client'

// Copy for the push action's assignee hint. Branches are annotated with the
// row numbers from SUP-2348's state table (13 rows), so this file can be
// re-checked against the spec without re-deriving which branch is which. The
// short descriptions travel with the numbers in case the table is renumbered.
// Kept as a pure function so every
// resolution state is unit-tested without mounting the editor — the states are
// the point of the feature, and several of them (nobody on call, nobody
// mapped) only appear against real schedule data that is awkward to stage in a
// component test.
export type AssigneeHintTone = 'muted' | 'warning'

export type AssigneeHint = {
  // One line, shown inline under the field.
  summary: string
  // Longer explanation shown in the hover tooltip. Empty when the summary
  // already says everything.
  detail: string
  tone: AssigneeHintTone
}

// "As of Aug 6, 2:15 PM PT" — resolution happens at dispatch, so the editor is
// always showing a snapshot. Stamping it with the company's clock (not the
// admin's browser) keeps it honest for an admin in another timezone, and
// avoids the word "today", which is wrong for an evening edit near the
// company's day boundary.
export function formatResolvedAt(at: Date, timeZone?: string | null): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }
  if (timeZone) options.timeZone = timeZone
  try {
    return `As of ${new Intl.DateTimeFormat('en-US', options).format(at)}`
  } catch {
    // An unknown IANA zone from company config shouldn't blank the hint.
    delete options.timeZone
    return `As of ${new Intl.DateTimeFormat('en-US', options).format(at)}`
  }
}

const withValue = (name: string, value: string | null) =>
  value ? `${name}: ${value}` : name

// What happens when the directory produced nothing — the integration's own
// default, or an empty assignee on the pushed task.
function fallbackClause(preview: DirectoryAssigneePreview): string {
  return preview.value
    ? `the integration default ${preview.value} will be used instead.`
    : 'no assignee will be set on the pushed task.'
}

export function describeAssigneePreview(
  preview: DirectoryAssigneePreview,
  integrationName: string,
): AssigneeHint {
  const { staff, coveringFor, roleName, schedulingMode, candidates } = preview
  const isRotation = schedulingMode === 'ROTATION'
  const rotationHasNoFallbackWarning =
    'This on-call rotation has no fallback — no one will be notified on days where no staff member is explicitly assigned.'

  // Row 1: manual text override set.
  if (preview.source === 'manual') {
    return {
      summary: `Manual override: ${preview.value}`,
      detail:
        'This value is pushed as typed. Directory resolution is skipped while it is set.',
      tone: 'muted',
    }
  }

  // Somebody resolved and had a code mapped. `metadata-default` also carries a
  // value and a staff member, but that value is the integration's default and
  // the staff member is whoever was *missing* a code — handled further down.
  if (preview.value && staff && preview.source !== 'metadata-default') {
    // Row 8: ROTATION, day blank, fallback set.
    if (preview.source === 'rotation-fallback') {
      return {
        summary: `No one is assigned today — the rotation fallback ${withValue(staff.name, preview.value)} will be used.`,
        detail: roleName ? `Resolved through the ${roleName} role.` : '',
        tone: 'muted',
      }
    }

    if (isRotation) {
      const covering = coveringFor ? ` (covering for ${coveringFor.name})` : ''
      // A ROTATION role only resolves several people through a substitute
      // chain — "role order" would be meaningless here, so the overlap warning
      // is worded for substitutes instead.
      // Row 12: ROTATION, multiple substitutes covering. Warns, never blocks.
      if (candidates.length > 1) {
        return {
          summary: `More than one substitute is covering right now. The first will be assigned: ${withValue(staff.name, preview.value)}`,
          detail: `Substitutes are used in the order configured on the time-off entry${roleName ? ` for ${roleName}` : ''}.`,
          tone: 'warning',
        }
      }
      // Rows 5 and 6: ROTATION on-call assignee has a mapped ID — row 6 is the
      // same line with the substitute's "(covering for X)" suffix.
      return {
        summary: `On call right now: ${withValue(staff.name, preview.value)}${covering}`,
        detail: roleName ? `Resolved through the ${roleName} role.` : '',
        tone: 'muted',
      }
    }

    // Row 11: FAN_OUT, more than one member on. Warns, never blocks.
    if (schedulingMode === 'FAN_OUT' && candidates.length > 1) {
      return {
        summary: `More than one staff member is active in this role. The first staff member in the role order will be assigned: ${withValue(staff.name, preview.value)}`,
        detail: `Everyone active right now: ${candidates
          .map(candidate => withValue(candidate.name, candidate.value))
          .join(' / ')}. This does not block saving.`,
        tone: 'warning',
      }
    }

    // Rows 2 and 10: a staff member with a mapped ID, picked directly or
    // resolved as the single active member of a FAN_OUT role.
    return {
      summary: `Will assign ${withValue(staff.name, preview.value)}`,
      detail: roleName ? `Resolved through the ${roleName} role.` : '',
      tone: 'muted',
    }
  }

  // Rows 3, 4 and 7: someone resolved, but nobody in the chain had a code for
  // this integration. Rows 3/4 differ only by whether an integration default
  // exists (fallbackClause); row 7 is the ROTATION wording of the same state.
  if (staff) {
    const lead = isRotation
      ? `${staff.name} is on call but has no ${integrationName} ID mapped`
      : `${staff.name} has no ${integrationName} ID mapped`
    return {
      summary: `${lead} — ${fallbackClause(preview)}`,
      detail: `Add an ID on the staff member in Settings → Team to have it pushed automatically.`,
      tone: 'warning',
    }
  }

  // A role was referenced but resolved to nobody at all.
  if (roleName) {
    // Row 9: ROTATION, day blank, no fallback — the detail line is what
    // separates "nobody today" from "this rotation reaches nobody, ever".
    if (isRotation) {
      return {
        summary: `No one is on call right now for ${roleName} — ${fallbackClause(preview)}`,
        detail: preview.hasRotationFallback
          ? 'The rotation fallback is unavailable right now too.'
          : rotationHasNoFallbackWarning,
        tone: 'warning',
      }
    }
    // Row 13: FAN_OUT, nobody on — the AVAILABLE window excludes right now.
    return {
      summary: `No one in ${roleName} is available right now — ${fallbackClause(preview)}`,
      detail:
        'Availability windows on the role or its members exclude the current time.',
      tone: 'warning',
    }
  }

  // Not in the table: nothing picked and nothing linked at all — the ordinary
  // state for an action that simply uses the integration default.
  return {
    summary: preview.value
      ? `No directory contact linked — the integration default ${preview.value} will be used.`
      : 'No directory contact linked and no integration default — no assignee will be set.',
    detail: preview.value
      ? ''
      : 'Pick a staff member or role above, or set a default on the integration.',
    tone: preview.value ? 'muted' : 'warning',
  }
}
